import { useId, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { AdminDrawer } from "@admin/_components/AdminDrawer";
import { Alert } from "@common/Alert";
import { Button, Icon } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { LocationStatus } from "@models/location";
import type { Location, OrganizationDetail } from "@models/organization";
import { isSameGuid } from "@utils/sameGuid";
import { downloadErrorMessage } from "../_helpers/certReview";
import {
    editLocationRefusal,
    type EditLocationRefusal,
} from "../_helpers/editLocation";
import {
    isStateChange,
    locationFormSchema,
    toLocationFormValues,
    toUpdateLocationAddressRequest,
    type LocationFormValues,
} from "../_helpers/locationForm";
import { useFileDownload } from "../_helpers/useFileDownload";
import { AddressFormFields } from "./AddressFormFields";
import { FormField } from "./FormField";
import { LocationAddressHistory } from "./LocationAddressHistory";

export interface EditLocationSaved {
    // A change of state moved the location back to review. False for a
    // correction, for a location already in review, and when the 200 carried
    // no answer.
    sentBackToReview: boolean;
}

interface EditLocationDrawerProps {
    // The client detail's location when the drawer opened: the form is filled
    // from it and the save carries its updatedAt.
    location: Location;
    onClose: () => void;
    // A 200: the page closes the drawer and re-reads the client.
    onSaved: (saved: EditLocationSaved) => void;
    // The API's 404, or a latest read without this location: it is gone (or
    // its client is). The page closes the drawer and says so.
    onGone: () => void;
    // Reads the client again for "Load latest"; null when that read failed.
    loadLatest: () => Promise<OrganizationDetail | null>;
}

// C6 (API #433): a platform admin edits a client location's address. Street,
// city and ZIP are corrections; a change of state sends the location back to
// review and needs a resale certificate for the new state. A refusal keeps
// the drawer open with the admin's edits and says why; a stale copy
// (location_changed) offers the latest details instead of saving over them.
export const EditLocationDrawer = ({
    location,
    onClose,
    onSaved,
    onGone,
    loadLatest,
}: EditLocationDrawerProps) => {
    const { updateLocationAddress, downloadLocationFile } = useLocationApi();
    const runGuarded = useSubmitGuard();
    const formId = useId();
    const certificateInputId = useId();

    // The read the form was filled from, whose updatedAt the save sends back
    // verbatim. Replaced only by "Load latest".
    const [readAs, setReadAs] = useState(location);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingLatest, setIsLoadingLatest] = useState(false);
    const [refusal, setRefusal] = useState<EditLocationRefusal | null>(null);
    const [latestFailed, setLatestFailed] = useState(false);
    const [showsLatest, setShowsLatest] = useState(false);

    const methods = useForm<LocationFormValues>({
        resolver: zodResolver(locationFormSchema),
        defaultValues: toLocationFormValues(location),
    });
    const {
        register,
        watch,
        formState: { errors },
    } = methods;
    const changesState = isStateChange({
        state: watch("state"),
        loadedState: watch("loadedState"),
    });

    const {
        downloadingId,
        error: downloadError,
        start: startDownload,
    } = useFileDownload(
        readAs.id,
        (fileId) => downloadLocationFile(readAs.id, fileId),
        (error) => downloadErrorMessage(error, "certificates"),
    );
    const newestCertificate = readAs.newestCertificate ?? null;

    const isProcessing = isSaving || isLoadingLatest;

    const save = (values: LocationFormValues) =>
        runGuarded(async () => {
            setRefusal(null);
            setLatestFailed(false);
            setShowsLatest(false);
            setIsSaving(true);

            try {
                const response = await updateLocationAddress(
                    readAs.id,
                    toUpdateLocationAddressRequest(values, readAs),
                );
                onSaved({
                    sentBackToReview:
                        typeof response === "object" &&
                        response.sentBackToReview === true,
                });
            } catch (error) {
                const refused = editLocationRefusal(error);
                if (refused.kind === "gone") {
                    onGone();
                    return;
                }
                // Said at the certificate field while it is on screen; a
                // field that is not would hide it, so the drawer says it.
                if (refused.kind === "certificate" && isStateChange(values)) {
                    methods.setError("resaleCertificate", {
                        type: "server",
                        message: refused.message,
                    });
                    return;
                }
                setRefusal(refused);
            } finally {
                setIsSaving(false);
            }
        });

    const showLatest = () =>
        runGuarded(async () => {
            setLatestFailed(false);
            setIsLoadingLatest(true);

            try {
                const latestDetail = await loadLatest();
                if (!latestDetail) {
                    setLatestFailed(true);
                    return;
                }
                const latest = latestDetail.locations.find((candidate) =>
                    isSameGuid(candidate.id, readAs.id),
                );
                if (!latest) {
                    onGone();
                    return;
                }
                setReadAs(latest);
                methods.reset(toLocationFormValues(latest));
                setRefusal(null);
                setShowsLatest(true);
            } finally {
                setIsLoadingLatest(false);
            }
        });

    return (
        <AdminDrawer
            title="Edit Location"
            onClose={onClose}
            isProcessing={isProcessing}
            footer={
                <div className="flex items-center gap-4">
                    {/* Outside the form, so tied to it by `form`. A native
                        button: Common Button does not forward `form`. */}
                    <button
                        type="submit"
                        form={formId}
                        disabled={isProcessing}
                        className="btn btn-primary"
                    >
                        {isSaving && (
                            <span className="loading loading-spinner" />
                        )}
                        {isSaving ? "Saving…" : "Save Changes"}
                    </button>
                    {/* text-neutral: a ghost button on a light surface is
                        otherwise white on white (see the UI CLAUDE.md). */}
                    <Button
                        color={ComponentColor.Ghost}
                        className="text-neutral"
                        disabled={isProcessing}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                </div>
            }
        >
            {refusal && (
                <Alert color={ComponentColor.Error} className="mb-4">
                    <p>{refusal.message}</p>
                    {refusal.kind === "location_changed" && (
                        <>
                            {latestFailed && (
                                <p className="mt-2">
                                    The latest details couldn&apos;t be loaded.
                                    Try again.
                                </p>
                            )}
                            <Button
                                size={ComponentSize.SM}
                                outline
                                className="mt-2"
                                disabled={isProcessing}
                                onClick={() => void showLatest()}
                            >
                                {isLoadingLatest ? "Loading…" : "Load latest"}
                            </Button>
                        </>
                    )}
                </Alert>
            )}
            {showsLatest && (
                <p role="status" className="mb-4 text-sm font-medium">
                    These are the latest details. Make your changes again.
                </p>
            )}

            <div className="mb-4">
                <p className="mb-1 text-sm font-semibold">Name</p>
                <p>{readAs.name}</p>
            </div>

            <FormProvider {...methods}>
                <form
                    id={formId}
                    noValidate
                    onSubmit={methods.handleSubmit((values) => save(values))}
                >
                    <fieldset
                        disabled={isProcessing}
                        className="flex flex-col gap-4"
                    >
                        <AddressFormFields />
                        {changesState && (
                            <>
                                <p className="text-sm">
                                    {readAs.status === LocationStatus.Pending
                                        ? "Changing the state needs a resale certificate for the new state."
                                        : "Changing the state sends this location back to review, and it needs a resale certificate for the new state."}
                                </p>
                                <FormField
                                    id={certificateInputId}
                                    label="Resale Certificate"
                                    error={errors.resaleCertificate?.message}
                                >
                                    {/* Unregistered when the state goes back:
                                        a correction sends no file. */}
                                    <input
                                        id={certificateInputId}
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        className="file-input file-input-bordered w-full bg-white text-neutral"
                                        {...register("resaleCertificate", {
                                            shouldUnregister: true,
                                        })}
                                    />
                                    <p className="mt-1 text-xs text-neutral/60">
                                        PDF, JPG, or PNG, up to 10 MB.
                                    </p>
                                </FormField>
                            </>
                        )}
                    </fieldset>
                </form>
            </FormProvider>

            <div className="mt-4">
                {newestCertificate ? (
                    <>
                        <Button
                            color={ComponentColor.Primary}
                            outline
                            size={ComponentSize.SM}
                            loading={downloadingId === newestCertificate.id}
                            disabled={downloadingId !== null}
                            onClick={() => void startDownload(newestCertificate)}
                        >
                            <Icon name="description" />
                            View/Download Certificate
                        </Button>
                        {downloadError?.fileId === newestCertificate.id && (
                            <p role="alert" className="mt-1 text-sm text-error">
                                {downloadError.message}
                            </p>
                        )}
                    </>
                ) : (
                    <p className="text-sm text-neutral/70">
                        No certificate on file.
                    </p>
                )}
            </div>

            <LocationAddressHistory
                locationId={readAs.id}
                changes={readAs.addressChanges ?? []}
            />
        </AdminDrawer>
    );
};
